import { select, call, takeLatest } from 'redux-saga/effects';

import { OrderExpiredReportActions } from '../constants';
import { OrderExpiredReportApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteOrderExpiredReportAction,
    GetOrderExpiredReportAction,
    GetOrderExpiredReportsAction,
} from '../actions/orderExpiredReport-actions';
import sortingService from '../../services/sorting';
import { handleError, resolveApiCall, ResolverApiFailure } from '../../services/api-handlers/api-resolver';
import { GetProviderReportsAction } from '../actions/providerReport-actions';
import { groupOrderExpiredToCsv } from 'src/helpers/groupCsvOrderExpired';
import { exportToCsv } from '../../services/exports/csv';
import { toast } from 'react-toastify';

function* handleFetchOrderExpiredReports(action: GetOrderExpiredReportsAction) {
    const oldData = yield select((state: StoreState) => state.orderExpiredReports.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        OrderExpiredReportApi.getOrderExpiredReports({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchOrderExpiredReportsCsv(action: GetOrderExpiredReportsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            OrderExpiredReportApi.getOrderExpiredReports({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetProviderReportsAction = {
                    type: OrderExpiredReportActions.FETCH_ORDER_EXPIRED_REPORTS_CSV,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchOrderExpiredReportsCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupOrderExpiredToCsv(dataAll);
                    exportToCsv('order-expired.csv', groupCsv);
                } else {
                    const groupCsvData = groupOrderExpiredToCsv(data.payload);
                    exportToCsv('order-expired.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchOrderExpiredReport(action: GetOrderExpiredReportAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => OrderExpiredReportApi.getOrderExpiredReport(id));
}

function* handleDeleteOrderExpiredReport(action: DeleteOrderExpiredReportAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => OrderExpiredReportApi.deleteOrderExpiredReport(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(OrderExpiredReportActions.FETCH_ORDER_EXPIRED_REPORTS, handleFetchOrderExpiredReports);
    yield takeLatest(OrderExpiredReportActions.FETCH_ORDER_EXPIRED_REPORTS_CSV, handleFetchOrderExpiredReportsCsv);
    yield takeLatest(OrderExpiredReportActions.FETCH_ORDER_EXPIRED_REPORT, handleFetchOrderExpiredReport);
    yield takeLatest(OrderExpiredReportActions.DELETE_ORDER_EXPIRED_REPORT, handleDeleteOrderExpiredReport);
}
